import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class RemoveEmailCycleRecipientRPC implements
  LeoRPC<RemoveEmailCycleRecipientRPC.Request, RemoveEmailCycleRecipientRPC.Response, RemoveEmailCycleRecipientRPC.Errors.Errors>
{
  abstract execute(
    request: RemoveEmailCycleRecipientRPC.Request
  ): Promise<LeoRPCResult<RemoveEmailCycleRecipientRPC.Response, RemoveEmailCycleRecipientRPC.Errors.Errors>>;
}

export namespace RemoveEmailCycleRecipientRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace Scope {
      export enum Scope {
        ONLY_THIS = "ONLY_THIS",
        THIS_AND_PENDING = "THIS_AND_PENDING",
      }
    
      export interface ScopeDTO {
        case: string;
      }
    
      export const fromDTO = (dto: ScopeDTO): Scope => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestScopeError(
            `Invalid DTO ${dto} provided for Scope.Scope`
          );
        }
        switch (dto.case) {
          case "ONLY_THIS":
            return Scope.ONLY_THIS;
          case "THIS_AND_PENDING":
            return Scope.THIS_AND_PENDING;
          default:
            throw new InvalidRequestScopeError(
              `Case ${dto.case} is not valid case of Scope.Scope`
            );
        }
      };
    
      export const toDTO = (scope: Scope): ScopeDTO => {
        const enumDTO = {
          case: Scope[scope],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    scheduleId: string;
    emailId: string;
    scope: RequestEnums.Scope.ScopeDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isRemoveEmailCycleRecipientRPCRequest: boolean = true;
  
    readonly scheduleId: LeoUUID;
    readonly emailId: LeoUUID;
    readonly scope: RequestEnums.Scope.Scope;
  
  
    constructor(
    scheduleId: LeoUUID, 
    emailId: LeoUUID, 
    scope: RequestEnums.Scope.Scope
    ) {
      this.scheduleId = scheduleId;
      this.emailId = emailId;
      this.scope = scope;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const scheduleId = getLeoUuid(dto, "scheduleId", InvalidRequestScheduleIdError);
      const emailId = getLeoUuid(dto, "emailId", InvalidRequestEmailIdError);
      const scope = RequestEnums.Scope.fromDTO(getObject(dto, "scope", InvalidRequestScopeError));
      return new Request(
        scheduleId,
        emailId,
        scope
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          scheduleId: this.scheduleId.toDTO(),
          emailId: this.emailId.toDTO(),
          scope: RequestEnums.Scope.toDTO(this.scope)
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `RemoveEmailCycleRecipientRPC(scheduleId='${this.scheduleId}',emailId='${this.emailId}',scope='${this.scope}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `RemoveEmailCycleRecipientRPC.Response()`;
  };

  export enum RPCError {
    InvalidScheduleId = "INVALID_SCHEDULE_ID",
    InvalidEmailId = "INVALID_EMAIL_ID",
    ScheduleInProgress = "SCHEDULE_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface InvalidScheduleIdDTO {
      code: string;
    }
    
    export class InvalidScheduleId extends LeoRPCError<RPCError> {
      readonly isInvalidScheduleId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidScheduleId;
        super(code);
      }
    
    
      toDTO(): InvalidScheduleIdDTO {
        const dto: InvalidScheduleIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidScheduleId.prototype.toString = function toString(): string {
      return `RemoveEmailCycleRecipientRPC.INVALID_SCHEDULE_ID()`;
    };
    
    
    export interface InvalidEmailIdDTO {
      code: string;
    }
    
    export class InvalidEmailId extends LeoRPCError<RPCError> {
      readonly isInvalidEmailId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidEmailId;
        super(code);
      }
    
    
      toDTO(): InvalidEmailIdDTO {
        const dto: InvalidEmailIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidEmailId.prototype.toString = function toString(): string {
      return `RemoveEmailCycleRecipientRPC.INVALID_EMAIL_ID()`;
    };
    
    
    export interface ScheduleInProgressDTO {
      code: string;
    }
    
    export class ScheduleInProgress extends LeoRPCError<RPCError> {
      readonly isScheduleInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ScheduleInProgress;
        super(code);
      }
    
    
      toDTO(): ScheduleInProgressDTO {
        const dto: ScheduleInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ScheduleInProgress.prototype.toString = function toString(): string {
      return `RemoveEmailCycleRecipientRPC.SCHEDULE_IN_PROGRESS()`;
    };
    

    export type Errors = InvalidScheduleId | InvalidEmailId | ScheduleInProgress;
    export type ErrorsDTO = InvalidScheduleIdDTO | InvalidEmailIdDTO | ScheduleInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidScheduleId:
          return new InvalidScheduleId();
        case RPCError.InvalidEmailId:
          return new InvalidEmailId();
        case RPCError.ScheduleInProgress:
          return new ScheduleInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestScheduleIdError extends RemoveEmailCycleRecipientRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScheduleIdError";
  }
}

export class InvalidRequestEmailIdError extends RemoveEmailCycleRecipientRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailIdError";
  }
}

export class InvalidRequestScopeError extends RemoveEmailCycleRecipientRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScopeError";
  }
}

export class InvalidRequestScopeOnlyThisError extends InvalidRequestScopeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScopeOnlyThisError";
  }
}

export class InvalidRequestScopeThisAndPendingError extends InvalidRequestScopeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScopeThisAndPendingError";
  }
}


export class InvalidResponseInvalidScheduleIdError extends RemoveEmailCycleRecipientRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidScheduleIdError";
  }
}

export class InvalidResponseInvalidEmailIdError extends RemoveEmailCycleRecipientRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidEmailIdError";
  }
}

export class InvalidResponseScheduleInProgressError extends RemoveEmailCycleRecipientRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseScheduleInProgressError";
  }
}

